/**
 * Canvas pan/zoom transform math (Story 37.9, Task 3 — AC #3).
 *
 * Pure, DOM-free extraction of the two transform formulas that previously lived
 * INLINE inside `useCanvasPanZoom` (`$reportId.edit.tsx`). They were untestable
 * there: the inline hook reads `getBoundingClientRect()`, which returns all-zero
 * rects in jsdom (jsdom paints no pixels), so the `width === 0` guard always
 * early-returned and a jsdom unit test of the hook proved nothing (Pre-mortem B).
 *
 * Extracting the math into pure functions that take rects/pan as PLAIN ARGS lets
 * us unit-test the formulas deterministically (no DOM, no mocks) — the same
 * leaf-extraction discipline as `reportLayout.ts` / `reportStaleTime.ts` /
 * `reportClientSource.ts`. The hook now reads the rects and delegates here, so
 * its behavior is unchanged (behavior-preserving — AC #8). The genuinely
 * DOM-layout-dependent parts (real grab-to-pan drag, real wheel/ctrl-wheel zoom,
 * initial centering on mount) are covered by e2e, where rects are real.
 *
 * Both formulas are transcribed VERBATIM from the original inline hook so the
 * editor pans/zooms identically:
 *   - zoomAtPoint: `$reportId.edit.tsx` (the cursor-anchoring formula)
 *   - clampPan:    `$reportId.edit.tsx` (the EDGE-bounds formula + zero-rect guard)
 */

/** Default px of canvas content that must stay visible on every edge. */
export const PAN_EDGE_PX = 120;

export interface Point {
  x: number;
  y: number;
}

/** Minimal rect shape the formulas consume (a subset of DOMRect). */
export interface RectSize {
  width: number;
  height: number;
}

/**
 * Canonical zoom-to-pointer: returns the new pan so the content point currently
 * under the cursor stays fixed across a scale change.
 *
 * Transcribed verbatim from the inline hook:
 *   contentX = (cx - p.x) / oldScale; contentY = (cy - p.y) / oldScale
 *   next = { x: cx - contentX*newScale, y: cy - contentY*newScale }
 * where `cx = clientX - containerRect.left`, `cy = clientY - containerRect.top`
 * (the cursor position relative to the container's top-left).
 *
 * Pure: no `ref`, no `getBoundingClientRect` — the caller reads the rect and
 * passes only `{ left, top }`.
 *
 * @param oldScale - the current zoom scale (e.g. 1.0 = 100%).
 * @param newScale - the target zoom scale.
 * @param cursor   - the focal point in VIEWPORT coords (clientX/clientY).
 * @param containerRect - the container's `{ left, top }` (viewport offset).
 * @param pan     - the current pan offset.
 * @returns the new pan that keeps the focal content point under the cursor.
 */
export function zoomAtPoint(
  oldScale: number,
  newScale: number,
  cursor: Point,
  containerRect: { left: number; top: number },
  pan: Point,
): Point {
  // Defensive guard for the now-public pure API: a zero / non-finite `oldScale`
  // would divide-by-zero and yield a non-finite pan (which would corrupt the
  // transform). The wired caller always passes a clamped scale (≥ 0.5), so this
  // never fires in production — but a public reusable fn must not return NaN
  // (code-review R1). On a degenerate scale, leave the pan unchanged.
  if (!Number.isFinite(oldScale) || oldScale === 0 || !Number.isFinite(newScale)) {
    return pan;
  }
  // Cursor position relative to the container's top-left.
  const cx = cursor.x - containerRect.left;
  const cy = cursor.y - containerRect.top;
  // Content point currently under the cursor.
  const contentX = (cx - pan.x) / oldScale;
  const contentY = (cy - pan.y) / oldScale;
  // New pan so that same content point stays under the cursor at the new scale.
  return { x: cx - contentX * newScale, y: cy - contentY * newScale };
}

/**
 * Clamp a candidate pan so the paper can never be dragged fully off-screen: keep
 * at least `edge` px of content visible on every side.
 *
 * Transcribed verbatim from the inline hook:
 *   EDGE = 120
 *   minX = EDGE - wRect.width;  maxX = cRect.width  - EDGE
 *   minY = EDGE - wRect.height; maxY = cRect.height - EDGE
 *   clamp next.{x,y} into [min, max]
 *
 * Guard: when either rect is zero-size (always true in jsdom — no painting),
 * return the candidate UNCHANGED. This is the jsdom-safe no-op the original
 * hook relies on, preserved here so the route's behavior is identical.
 *
 * @param next         - the candidate pan.
 * @param containerRect - the scroll container size (`cRect`).
 * @param wrapperRect   - the zoom-wrapper (paper) size (`wRect`).
 * @param edge          - px of content kept visible per side (default 120).
 * @returns the clamped pan, or `next` unchanged when a rect is zero-size.
 */
export function clampPan(
  next: Point,
  containerRect: RectSize,
  wrapperRect: RectSize,
  edge: number = PAN_EDGE_PX,
): Point {
  // jsdom-safe guard: a zero-size rect on EITHER axis → cannot compute valid
  // bounds, return as-is. The original inline hook checked `.width` only; a
  // zero-HEIGHT (non-zero-width) rect would then run the Y math with
  // `minY = edge - 0 = edge` and could invert the Y bounds. Guarding both axes
  // is strictly safer and a no-op for any real painted container (code-review R1).
  if (
    containerRect.width === 0 ||
    wrapperRect.width === 0 ||
    containerRect.height === 0 ||
    wrapperRect.height === 0
  ) {
    return next;
  }
  const minX = edge - wrapperRect.width;
  const maxX = containerRect.width - edge;
  const minY = edge - wrapperRect.height;
  const maxY = containerRect.height - edge;
  return {
    x: Math.min(maxX, Math.max(minX, next.x)),
    y: Math.min(maxY, Math.max(minY, next.y)),
  };
}
